/**
 * PATH: packages/tenant-shared/src/email/api.ts
 * FILE_ID: TENANTSHARED-EMAILAPI-001
 * PURPOSE: Tenant-agnostic email API client wrapper around
 *          `quotechApi`. Sets/holds the active tenant id used in
 *          email-namespaced backend calls.
 * WHY:     Byte-identical between newmark and sam pre-MR #7. The
 *          underlying backend endpoints are tenant-aware via the
 *          tenant id parameter, not via separate code paths, so two
 *          clones served no purpose.
 * DEPENDENCIES:
 *   - ../services/quotechApi — the actual axios-style client (lifted
 *     in the same MR; was `@/shared/services/quotechApi` in tenants).
 * RELATED:
 *   - apps/{newmark,sam}/src/features/email/lib/api.ts — thin
 *     re-export shims (post-MR #7).
 */

import { quotechFetch, quotechJson, type QuotechRequestOptions } from '../services/quotechApi'

/**
 * Additive FE twin of the frozen MailboxContextV1 wire contract.
 * Runtime publication remains RL-01/RL-02 work; this shape prevents surfaces
 * from inventing partial organisation-only context.
 */
export interface MailboxContextV1 {
  schema_version: 'email-mailbox-context.v1'
  publication_id: string
  user_id: number
  organisation_id: number
  selected_mailbox_key: string
  provider: 'gmail' | 'outlook' | 'legacy'
  provider_account_id?: number | null
  visible_mailbox_account_ids: number[]
  visibility_scope: 'selected_mailbox' | 'all_visible_mailboxes'
  privileged_scope_used: boolean
  context_fingerprint: string
  published_at: string
}

let _tenantId: number | null = null
let _tenantGeneration = 0
/** Visible-mailbox scope fingerprint (sorted keys). Null = all mailboxes. */
let _mailboxScope: string | null = null

export function setEmailTenantId(id: number | null) {
  if (_tenantId !== id) _tenantGeneration += 1
  _tenantId = id
}

/** Read the currently-bound email tenant (organisation) id.
 *  BUG-2026-07-17-EMAIL-ORG-SWITCH-STALE-MAILBOX — the email hub's
 *  SWR seed/persist caches must carry the org dimension so a snapshot
 *  written under one organisation can never instant-paint after the
 *  operator switches to another. */
export function getEmailTenantId(): number | null {
  return _tenantId
}

export function getEmailTenantGeneration(): number {
  return _tenantGeneration
}

export class StaleEmailTenantResponseError extends Error {
  constructor() {
    super('Email response belongs to a superseded organisation context.')
    this.name = 'StaleEmailTenantResponseError'
  }
}

function assertCurrentTenantGeneration(generation: number): void {
  if (_tenantGeneration !== generation) {
    throw new StaleEmailTenantResponseError()
  }
}

/**
 * RL-01 — publish the active mailbox visibility scope alongside org.
 * Scope is a stable sorted join of visible mailbox keys (or '' for
 * "all mailboxes"). Inbox query params already carry
 * ``synced_by_account_id__in``; this singleton lets meta caches and
 * remount keys share the same identity.
 */
export function setEmailMailboxScope(scope: string | null) {
  _mailboxScope = scope
}

export function getEmailMailboxScope(): string | null {
  return _mailboxScope
}

// Accept the full QuotechRequestOptions (superset of RequestInit) so callers
// can opt fail-soft, non-data calls OUT of the tab-wide identity-mismatch
// reload via `skipIdentityAssert` (BUG-2026-06-18-GMAIL-STATUS-IDENTITY-RELOAD-LOOP).
// A cosmetic connection-status poll must never seize the tab into a reload loop.
export async function emailApi<T>(
  endpoint: string,
  options: QuotechRequestOptions = {},
): Promise<T> {
  const tenantGeneration = _tenantGeneration
  const { body, headers: rawHeaders, ...init } = options
  const opts: Parameters<typeof quotechJson>[1] = {
    ...init,
    body: body as any,
    headers: rawHeaders,
    includeTenant: _tenantId != null,
    tenantId: _tenantId,
  }

  const { data } = await quotechJson<T>(endpoint, opts)
  assertCurrentTenantGeneration(tenantGeneration)
  return data
}

export async function emailApiFetch(path: string, opts: QuotechRequestOptions = {}): Promise<Response> {
  const tenantGeneration = _tenantGeneration
  const { body, headers: rawHeaders, ...init } = opts
  const response = await quotechFetch(path, {
    ...init,
    body: body as any,
    headers: rawHeaders,
    includeTenant: _tenantId != null,
    tenantId: _tenantId,
  })
  assertCurrentTenantGeneration(tenantGeneration)
  return response
}

// ─── Mark-as-bill contract (shared by @quotech/email-hub + @quotech/bills) ──
// ONE source of truth for the "this email is a bill" endpoint, so the two
// consumers (the email hub's hub-local hook + the @quotech/bills package's
// useMarkEmailAsBill) can never drift on the path or the request body. Each
// consumer still issues the call with ITS OWN client (emailApi vs billsApi),
// so this changes no auth/transport behaviour — it only de-duplicates the
// contract. Backend: POST /api/emails/<id>/mark-as-bill/.
export function markEmailAsBillPath(emailId: string): string {
  return `/api/emails/${emailId}/mark-as-bill/`
}

export function buildMarkAsBillBody(
  attachmentId?: string | number | null,
): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (attachmentId !== undefined && attachmentId !== null && attachmentId !== '') {
    body.attachment_id = attachmentId
  }
  return body
}

// ─── Cross-package shared types ────────────────────────────────────
//
// These live here (rather than inside the email feature directories or
// inside @quotech/email-hub) because BOTH @quotech/email-hub AND
// @quotech/kanban-host need them. Putting them in either package
// would create a circular workspace dependency:
//
//     email-hub → kanban-host → email-hub
//
// tenant-shared sits below both packages in the dep graph, so both
// can pull these types without cycles.
//
// Lifted from apps/newmark/src/features/email/hooks/useEmails.ts in
// MR-K4-partial (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
// The previous SAM tenant had this type missing (it was part of the
// in-flight bidirectional merge — sam's useEmails.ts did NOT export
// it). After this MR both tenants pull the type from one place.

/**
 * Render-ready snapshot of an email's current Kanban allocation,
 * sized to power the row chip + the AI tick/cross row.
 *
 * `source` mirrors `AllocationSource` on the backend:
 *   - 'AI_AUTO'                — AI allocated, no human review needed
 *   - 'AI_SUGGESTED_PENDING'   — AI proposed, awaiting tick/cross
 *   - 'AI_SUGGESTED_USER_CONFIRMED' — human accepted the AI proposal
 *   - 'USER_MANUAL'            — human picked a board manually
 *   - 'RULE'                   — deterministic rule fired
 *
 * `confidence` (0.0–1.0) is the LLM confidence captured at allocation
 * time. Always null for USER_MANUAL / RULE allocations (no LLM ran).
 *
 * BE source of truth: ``email_kanban.models.Allocation`` —
 * see backend ``email_kanban/models/allocation.py``.
 */
export interface EmailAllocationSummary {
  allocation_id: string
  card_id: string
  lane_id: string
  lane_name: string
  lane_kind: string
  lane_color: string | null
  board_id: string
  board_name: string
  board_kind: string
  board_color: string | null
  /** Sub-board breadcrumb. When the allocated board has a parent, the
   *  email is filed on a SUB-board and these carry the parent so the
   *  chip can render "Parent › Board · Lane" instead of hiding the
   *  hierarchy. Both null/absent for top-level boards (the common case)
   *  and on older cached payloads, so consumers must treat them as
   *  optional. BE source: ``_build_allocation_summary_map`` →
   *  ``Board.parent_board``. */
  parent_board_id?: string | null
  parent_board_name?: string | null
  source:
    | 'AI_AUTO'
    | 'AI_SUGGESTED_PENDING'
    | 'AI_SUGGESTED_USER_CONFIRMED'
    | 'USER_MANUAL'
    | 'RULE'
  confidence: number | null
  /** FEAT-2026-06-10-AI-TRIAGE-BANNER — card lifecycle + low-confidence
   *  review state. Optional: older cached payloads won't carry them. */
  card_status?: 'OPEN' | 'SNOOZED' | 'BLOCKED' | 'DONE' | 'ARCHIVED'
  /** True when the router parked the card on a review/landing lane
   *  because confidence was below threshold. */
  needs_review?: boolean
  /** The lane the AI would have picked, when ``needs_review``. */
  suggested_lane_name?: string | null
}
